/**
 * A lista que não se move debaixo do cursor (H07).
 *
 * ── A armadilha, escrita na task ──────────────────────────────────────────
 *
 * *"Reordenar a lista a cada mensagem nova. O item que o vendedor ia clicar sai
 * do lugar, e ele abre a conversa errada — várias vezes por dia."*
 *
 * É o defeito mais barato de introduzir e o mais irritante de conviver. Basta
 * o caminho óbvio: o SSE avisa, a consulta rebusca, o servidor devolve ordenado
 * por última mensagem, e a tela renderiza. Tudo certo em cada passo, e o
 * resultado é o vendedor abrindo a conversa do vizinho.
 *
 * ── O que este módulo faz ─────────────────────────────────────────────────
 *
 * Congela a ORDEM, não os dados. O conteúdo de cada linha continua atualizando
 * — prévia nova, contador de não lidas, etapa que mudou. O que não muda é a
 * POSIÇÃO, até o usuário pedir.
 *
 * Três situações, três tratamentos:
 *
 *   **Item que mudou** fica no lugar, marcado. O vendedor vê que chegou algo
 *   sem perder de vista o que ia clicar.
 *
 *   **Conversa nova** NÃO entra na lista. É contada, e a tela oferece mostrar.
 *   Inserir no topo empurra tudo para baixo, que é exatamente o movimento que a
 *   armadilha descreve.
 *
 *   **Item que saiu** — resolvido por outra pessoa, ou fora do filtro depois de
 *   uma mudança — continua visível e marcado, com o último conteúdo conhecido.
 *   Remover também é mover: tira uma linha e sobe todas as de baixo.
 *
 * ── Por que não usar apenas "não reordenar enquanto o mouse está em cima" ──
 *
 * Porque o cursor não é o único jeito de mirar. Quem navega por teclado tem
 * foco, quem usa toque não tem cursor nenhum, e quem está lendo a lista para
 * decidir também está mirando — sem tocar em nada. Congelar por interação
 * cobriria um caso e falharia nos outros três.
 */

export type MarcaDoItem = 'nenhuma' | 'atualizado' | 'saiu';

export interface ItemOrdenado<T> {
  readonly id: string;
  readonly item: T;
  readonly marca: MarcaDoItem;
}

export interface OrdemEstavel<T> {
  readonly itens: readonly ItemOrdenado<T>[];
  /** Conversas que existem no servidor e ainda não estão na lista. */
  readonly novas: number;
  /** A ordem do servidor, para quem quiser aplicá-la. */
  readonly ordemDoServidor: readonly string[];
}

export interface Congelado<T> {
  /** A ordem que está na tela. Vazia = primeira carga, usa a do servidor. */
  readonly ordem: readonly string[];
  /** O último conteúdo conhecido de cada item, para o que saiu continuar visível. */
  readonly conhecidos: ReadonlyMap<string, T>;
  /** A assinatura de conteúdo de cada item, para detectar o que mudou. */
  readonly assinaturas: ReadonlyMap<string, string>;
}

/**
 * O estado inicial, antes da primeira resposta do servidor.
 *
 * Genérico em vez de `Congelado<never>`: com `never` o mapa vazio não é
 * atribuível, e quem usa precisaria de uma asserção em cada chamada — asserção
 * que some da vista e depois vale para valor de verdade.
 */
export function congeladoVazio<T>(): Congelado<T> {
  return { ordem: [], conhecidos: new Map(), assinaturas: new Map() };
}

/**
 * Aplica a ordem congelada sobre o que o servidor devolveu.
 *
 * `assinaturaDe` decide o que conta como "mudou". Não é o objeto inteiro: a
 * resposta traz `projetado_em`, que muda a cada projeção mesmo sem nada
 * acontecer na conversa, e marcaria tudo como atualizado o tempo todo.
 */
export function aplicarOrdemEstavel<T>(
  congelado: Congelado<T>,
  doServidor: readonly T[],
  idDe: (item: T) => string,
  assinaturaDe: (item: T) => string,
): OrdemEstavel<T> {
  const ordemDoServidor = doServidor.map(idDe);
  const porId = new Map(doServidor.map((item) => [idDe(item), item] as const));

  // Primeira carga: nada a preservar, e a ordem do servidor é a certa.
  if (congelado.ordem.length === 0) {
    return {
      itens: doServidor.map((item) => ({ id: idDe(item), item, marca: 'nenhuma' as const })),
      novas: 0,
      ordemDoServidor,
    };
  }

  const itens = congelado.ordem.flatMap((id): ItemOrdenado<T>[] => {
    const atual = porId.get(id);

    if (!atual) {
      // Saiu do servidor. Continua visível com o último conteúdo conhecido —
      // remover subiria todas as linhas de baixo.
      const ultimo = congelado.conhecidos.get(id);
      return ultimo === undefined ? [] : [{ id, item: ultimo, marca: 'saiu' }];
    }

    const antes = congelado.assinaturas.get(id);
    const agora = assinaturaDe(atual);

    return [
      { id, item: atual, marca: antes !== undefined && antes !== agora ? 'atualizado' : 'nenhuma' },
    ];
  });

  const naOrdem = new Set(congelado.ordem);
  const novas = ordemDoServidor.filter((id) => !naOrdem.has(id)).length;

  return { itens, novas, ordemDoServidor };
}

/**
 * O congelado depois de o usuário aceitar a ordem nova.
 *
 * Recalcula tudo: ordem, conteúdo conhecido e assinaturas. É o único momento em
 * que a lista se move, e ele é sempre uma escolha de quem está olhando.
 */
export function congelarDoServidor<T>(
  doServidor: readonly T[],
  idDe: (item: T) => string,
  assinaturaDe: (item: T) => string,
): Congelado<T> {
  return {
    ordem: doServidor.map(idDe),
    conhecidos: new Map(doServidor.map((item) => [idDe(item), item] as const)),
    assinaturas: new Map(doServidor.map((item) => [idDe(item), assinaturaDe(item)] as const)),
  };
}

/**
 * Acrescenta a página seguinte SEM tocar no que já está na tela.
 *
 * A rolagem contínua é o caso fácil de estabilidade: página nova entra no fim, e
 * o que está acima não se mexe. O cuidado é não reordenar o que já estava — daí
 * a concatenação em vez de um congelamento novo.
 */
export function acrescentarPagina<T>(
  congelado: Congelado<T>,
  novaPagina: readonly T[],
  idDe: (item: T) => string,
  assinaturaDe: (item: T) => string,
): Congelado<T> {
  const jaTem = new Set(congelado.ordem);
  const novos = novaPagina.filter((item) => !jaTem.has(idDe(item)));

  const conhecidos = new Map(congelado.conhecidos);
  const assinaturas = new Map(congelado.assinaturas);
  for (const item of novos) {
    conhecidos.set(idDe(item), item);
    assinaturas.set(idDe(item), assinaturaDe(item));
  }

  return {
    ordem: [...congelado.ordem, ...novos.map(idDe)],
    conhecidos,
    assinaturas,
  };
}
